import {JSONModel, jsonProperty} from "../json.model";
import {ProgressBarModel} from "../misc/percentage-bar.model";
import {
  GqlModel,
  GqlModelListOperator,
  GqlModelOperator,
  GqlMutationModel,
  GqlQueryModel,
} from "../graphql/graphql.model";
import gql from "graphql-tag";
import {QuickFormArray, QuickFormGroup} from "../helpers/quick-form-group";
import {orderFormFragment, OrderFormGqlModel} from "./order-form.gql-model";
import {BreadcrumbGqlModel} from "../breadcrumbs/breadcrumb.gql-model";
import {SuccessResultGqlModel} from "../graphql/common.gql-model";

export const orderFormKeys = [
  "name",
  "shippingDestination",
  "deliveryMethod",
  "deliveryInformation",
  "estimatedHardwareOrderDate",
  "estimatedHardwareOrderDatePicker",
  "requestedDeliveryDate",
  "requestedDeliveryDatePicker",
  "sourceName",
  "sourceDocumentationLink",
];

export interface OrderListGqlVariables {
  projectId?: string;
  siteId?: string;
  id?: string;
  search?: string;
  limit?: number | string;
  page?: number | string;
  includeBreadcrumbs?: boolean;
}

export class OrderNumberAndDate extends JSONModel {
  @jsonProperty("string") orderNumber: string;
  @jsonProperty("stringDate") orderDate: string;
}

export const orderFragment: any = gql`
  fragment orderData on HardwareOrder {
    id
    name
    plannedAssets
    orderedAssets
    plannedAccessories
    orderedAccessories
    plannedItems
    orderedItems
    hardwareOrderDetails {
      orderNumber
      orderDate
    }
    hardwareOrderDate
    estimatedHardwareOrderDate
    siteHardwareOrderDate
    requestedDeliveryDate
    shippingDestination
    deliveryMethod
    deliveryInformation
    siteName
    projectName
    isMigrationActive
    siteId
    projectId
    workflowId
    sourceName
    sourceDocumentationLink
    orderedPercentageBar {
      visible
      title
      value
      type
      width
    }
    pastSchedule
    orderRequested
    orderPlaced
    orderComplete
    orderAcceptanceCompleted
    rejected
    rejectedReason
    rejectedDate
  }
`;

export class OrderGqlModel extends GqlModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") name: string;

  @jsonProperty(OrderFormGqlModel) orderForm: OrderFormGqlModel;

  @jsonProperty("number0") plannedAssets: number;
  @jsonProperty("number0") orderedAssets: number;
  @jsonProperty("number0") plannedAccessories: number;
  @jsonProperty("number0") orderedAccessories: number;
  @jsonProperty("number0") plannedItems: number;
  @jsonProperty("number0") orderedItems: number;

  @jsonProperty([OrderNumberAndDate]) hardwareOrderDetails: OrderNumberAndDate[];

  @jsonProperty("stringDate") hardwareOrderDate: string;
  @jsonProperty("stringDate") estimatedHardwareOrderDate: string;
  @jsonProperty("stringDate") estimatedHardwareOrderDatePicker: string;
  @jsonProperty("stringDate") siteHardwareOrderDate: string;
  @jsonProperty("stringDate") estimatedDeliveryDate: string;
  @jsonProperty("stringDate") targetDeliveryDate: string;
  @jsonProperty("stringDate") requestedDeliveryDate: string;
  @jsonProperty("stringDate") requestedDeliveryDatePicker: string;

  @jsonProperty("string") shippingDestination: string;
  @jsonProperty("string") deliveryMethod: string;
  @jsonProperty("string") deliveryInformation: string;
  @jsonProperty("boolean") shipCompleted: boolean;
  @jsonProperty("string") floorLevel: string;
  @jsonProperty("string") orderNotificationEmailList: string;
  @jsonProperty("string") extraPricingInformation: string;
  @jsonProperty("string") resellerInformation: string;
  @jsonProperty("string") resellerInstructions: string;

  @jsonProperty("string") siteName: string;
  @jsonProperty("string") projectName: string;
  @jsonProperty("boolean") isMigrationActive: boolean;
  @jsonProperty("string") siteId: string;
  @jsonProperty("string") projectId: string;
  @jsonProperty("string") workflowId: string;
  @jsonProperty("string") sourceName: string;
  @jsonProperty("string") sourceDocumentationLink: string;

  @jsonProperty(ProgressBarModel) orderedPercentageBar: ProgressBarModel;

  @jsonProperty("boolean") pastSchedule: boolean;
  @jsonProperty("string", "pastSchedule") pastScheduleMessage: string;

  @jsonProperty("boolean") orderRequested: boolean;
  @jsonProperty("boolean") orderPlaced: boolean;
  @jsonProperty("boolean") orderComplete: boolean;
  @jsonProperty("boolean") orderAcceptanceCompleted: boolean;

  @jsonProperty("boolean") rejected: boolean;
  @jsonProperty("string") rejectedReason: string;
  @jsonProperty("string") rejectedDate: string;

  form: QuickFormGroup = new QuickFormGroup(this, orderFormKeys);
}

export class OrderDataGqlModel extends GqlQueryModel {
  static mapper: GqlModelOperator<OrderDataGqlModel> = GqlModel.buildMapper(OrderDataGqlModel);

  static query: any = gql`
    ${orderFragment}

    ${orderFormFragment}

    ${BreadcrumbGqlModel.fragment}

    query getHardwareOrder($id: ID!, $includeOrderForm: Boolean!, $includeBreadcrumbs: Boolean!) {
      order: hardwareOrder(id: $id) {
        ...orderData
      }

      orderForm: hardwareOrderForm(id: $id) @include(if: $includeOrderForm) {
        ...orderFormData
      }

      breadcrumbs: hardwareOrderBreadcrumbs(orderId: $id) @include(if: $includeBreadcrumbs) {
        ...breadcrumbData
      }
    }
  `;

  @jsonProperty(OrderGqlModel) order: OrderGqlModel;
  @jsonProperty(OrderFormGqlModel) orderForm: OrderFormGqlModel;
  @jsonProperty([BreadcrumbGqlModel]) breadcrumbs: BreadcrumbGqlModel[];

  static variables(id: string, includeOrderForm: boolean = false, includeBreadcrumbs: boolean = false) {
    return {id, includeOrderForm, includeBreadcrumbs};
  }
}

export class OrderListGqlModel extends GqlQueryModel {
  static mapper: GqlModelOperator<OrderListGqlModel> = GqlModel.buildMapper(OrderListGqlModel);

  static query: any = gql`
    ${orderFragment}

    ${BreadcrumbGqlModel.fragment}

    query getHardwareOrders($projectId: ID, $siteId: ID, $id: ID, $search: String, $limit: Int, $page: Int, $includeBreadcrumbs: Boolean!) {
      rows: hardwareOrders(projectId: $projectId, siteId: $siteId, id: $id, search: $search, limit: $limit, page: $page) {
        ...orderData
      }

      count: hardwareOrderCount(projectId: $projectId, siteId: $siteId, id: $id, search: $search)

      breadcrumbs: hardwareOrderBreadcrumbs(projectId: $projectId, siteId: $siteId) @include(if: $includeBreadcrumbs) {
        ...breadcrumbData
      }
    }
  `;

  @jsonProperty([OrderGqlModel]) rows: OrderGqlModel[];
  @jsonProperty("number0") count: number;
  @jsonProperty([BreadcrumbGqlModel]) breadcrumbs: BreadcrumbGqlModel[];

  form: QuickFormArray = new QuickFormArray(this.rows, orderFormKeys);

  static variables(params: OrderListGqlVariables = {}) {
    const {projectId, siteId, id, search, limit, page, includeBreadcrumbs} = params;

    return {
      ...projectId && {projectId},
      ...siteId && {siteId},
      ...id && {id},
      ...search && {search},
      limit: +limit,
      page: +page,
      doCount: !id,
      includeBreadcrumbs: !!includeBreadcrumbs,
    };
  }
}

export interface OrderInputModel {
  id: string;
}

export class OrderUpdateGqlModel extends GqlModel implements OrderInputModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") siteId: string;
  @jsonProperty("string") name: string;
  @jsonProperty("string") shippingDestination: string;
  @jsonProperty("string") deliveryMethod: string;
  @jsonProperty("string") deliveryInformation: string;

  @jsonProperty("string") sourceName: string;
  @jsonProperty("string") sourceDocumentationLink: string;

  @jsonProperty("stringDate") hardwareOrderDate: string;
  @jsonProperty("stringDate") estimatedHardwareOrderDate: string;
  @jsonProperty("stringDate") estimatedDeliveryDate: string;
  @jsonProperty("stringDate") targetDeliveryDate: string;
  @jsonProperty("stringDate") requestedDeliveryDate: string;
}

export class OrderRemoveGqlModel extends GqlModel implements OrderInputModel {
  @jsonProperty("string") id: string;
  @jsonProperty("booleanTrue") remove: boolean;

  constructor(id: string) {
    super({id, remove: true});
  }
}

export class OrderRejectGqlModel extends GqlModel implements OrderInputModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") reject: string;

  constructor(id: string, reason: string) {
    super({id, reject: reason});
  }
}

export class OrderUnRejectGqlModel extends GqlModel implements OrderInputModel {
  @jsonProperty("string") id: string;
  @jsonProperty("booleanTrue") unReject: boolean;

  constructor(id: string) {
    super({id, unReject: true});
  }
}

export class RequestOrderPlaceGqlModel extends GqlModel implements OrderInputModel {
  @jsonProperty("string") id: string;
  @jsonProperty("booleanTrue") requestPlace: boolean;

  constructor(id: string) {
    super({id, requestPlace: true});
  }
}

export class ConfirmOrderPlacedGqlModel extends GqlModel implements OrderInputModel {
  @jsonProperty("string") id: string;
  @jsonProperty("booleanTrue") confirmPlaced: boolean;

  constructor(id: string) {
    super({id, confirmPlaced: true});
  }
}

export class OrderMutateGqlModel extends GqlMutationModel {
  static mapper: GqlModelOperator<OrderGqlModel> = GqlModel.buildMapper(OrderGqlModel, "hardwareOrder");

  @jsonProperty() order: OrderInputModel;

  static query = gql`
    ${orderFragment}

    mutation updateHardwareOrder($order: HardwareOrderInput!) {
      hardwareOrder: hardwareOrder(order: $order) {
        ...orderData
      }
    }
  `;

  constructor(order: OrderInputModel) {
    super({order});
  }
}

export class SiteListForOrderGqlModel extends GqlQueryModel {
  static mapper: GqlModelListOperator<SiteListForOrderGqlModel> =
    GqlModel.buildListMapper(SiteListForOrderGqlModel, "sites");

  static query: any = gql`
    query getSiteListForOrder($projectId: ID!) {
      sites: sites(projectId: $projectId) {
        id
        name
      }
    }
  `;

  @jsonProperty("string") id: string;
  @jsonProperty("string") name: string;

  static variables(projectId: string) {
    return {projectId};
  }
}

export class DeviceOrderSplitResultModel extends JSONModel {
  @jsonProperty("string") splitId: string;
  @jsonProperty("string") quantity: string;
  @jsonProperty("string") orderNumber: string;
  @jsonProperty("stringDate") orderDate: string;
}

export class DeviceOrderSplitMutateGqlModel extends GqlMutationModel {
  static mapper: GqlModelListOperator<DeviceOrderSplitResultModel> =
    GqlModel.buildListMapper(DeviceOrderSplitResultModel, "results");

  static query: any = gql`
    mutation splitDevices($deviceId: ID!, $orderId: ID!, $splitId: ID, $quantities: [Int], $auto: Boolean) {
      results: splitDeviceOnOrderForm(deviceId: $deviceId, orderId: $orderId, splitId: $splitId, quantities: $quantities, auto: $auto) {
        quantity
        splitId
        orderNumber
        orderDate
      }
    }
  `;

  @jsonProperty("string") deviceId: string;
  @jsonProperty("string") orderId: string;
  @jsonProperty("string") splitId: string;
  @jsonProperty(["number"]) quantities: number[];
  auto: boolean;

  constructor(data?: any) {
    super(data || {});

    this.auto = data && !data.quantities;
    this.splitId = this.splitId || null;
  }
}

export class HardwareOrderDetailsInputModel extends JSONModel {
  @jsonProperty("string") id: string;
  @jsonProperty("boolean") isStandaloneAccessory: boolean;
  @jsonProperty("string") splitId: string;
  @jsonProperty("string") orderNumber: string;
  @jsonProperty("date") orderDate: string;
}

export class HardwareOrderDetailsMutateGqlModel extends GqlMutationModel {
  static mapper: GqlModelOperator<SuccessResultGqlModel> = GqlModel.buildMapper(SuccessResultGqlModel, "result");

  static query: any = gql`
    mutation updateOrderNumbersAndDates($orderId: ID!, $details: [HardwareOrderDetailsInput]) {
      result: updateOrderNumbersAndDates(orderId: $orderId, details: $details) {
        success
      }
    }
  `;

  @jsonProperty("string") orderId: string;
  @jsonProperty([HardwareOrderDetailsInputModel]) details: HardwareOrderDetailsInputModel[];
}
